import { useMemo, useState } from 'react';

import { Dayjs } from 'dayjs';

import { range } from 'lodash';

import styled from 'styled-components';

import { Slider, Button, ConfigProvider } from 'antd';

import { CaretRightOutlined, PauseOutlined } from '@ant-design/icons';

import { TimelineType } from '@/store/P4RReducer';

import useP4RContext from '@/store/P4RContext';

import appTheme from '@/styles/theme';

const StyledSlider = styled(Slider)<{
  dotIndexes: number[];
  comparison: boolean;
  actualHour: number;
}>`
  .ant-slider-dot {
    height: 10px;
    width: 0;
    border-width: 0;
    border-radius: 0;
  }
  ${({ theme, dotIndexes }) =>
    dotIndexes
      .map(
        (step: number) => `
          .ant-slider-dot:nth-of-type(${step}) {
            height: 8px;
            width: 1px;
            background-color: ${theme.colorWhite};
          }
        `,
      )
      .join('')}

  ${({ comparison, actualHour }) =>
    !comparison
      ? `
        .ant-slider-dot:nth-of-type(${actualHour + 1}) {
          height: 9px;
          width: 3px;
          background-color: red;
          inset-block-start: -1px;
        }
      `
      : ''}
`;

const StyledMarks = styled.div`
  display: flex;
  .slider-marks-date {
    flex: 24;
    &:last-child {
      flex: 23;
    }
    flex-shrink: 0;
    min-width: 0;
    user-select: none;
    position: relative;
    /* border-left: 2px solid var(--ant-dark-background); */
    &:before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      width: 1px;
      height: 100%;
      transform: translate(-50%, 0);
      background-color: var(--ant-dark-background);
    }

    .slider-marks-date-inner {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      margin-left: 5px;
      line-height: 1;
      padding-top: 6px;
    }
  }
`;

export default function Timeline({
  startDate,
  comparison,
  currentTime,
  daysNumber,
  marks,
  sliderClass,
  handleCurrentTime,
  handleTimeLinePlay,
  controlDisabled,
  reducedPage,
}: {
  startDate: Dayjs | undefined;
  comparison?: boolean;
  currentTime: TimelineType;
  daysNumber: number;
  marks: Record<string, any>;
  sliderClass: string;
  handleCurrentTime: (value: number, manual: boolean) => void;
  handleTimeLinePlay: () => void;
  controlDisabled?: boolean;
  reducedPage?: boolean;
}) {
  const actualHour = startDate?.hour();

  const [isDragging, setIsDragging] = useState(false);

  const { timelineMode, timelinePlaying } = useP4RContext();

  const indexesNumber = 24 * daysNumber;

  const dotIndexes = useMemo(() => {
    const indexes = range(1, indexesNumber, 6);

    const dotIndexesSorted: number[] = [];

    indexes.forEach((step: number) => {
      if (step % 24 !== 0 && step !== 1) {
        dotIndexesSorted.push(step);
      }
    });

    return dotIndexesSorted;
  }, [indexesNumber]);

  const sliderColor =
    timelineMode === 'live'
      ? appTheme.token.colorPrimary
      : appTheme.token.colorSecondary;

  return (
    <div className={sliderClass}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column-reverse',
          flex: 1,
          minWidth: 480,
          transform: 'translate(-0.5px, 0)',
        }}
      >
        <ConfigProvider
          theme={{
            components: {
              Slider: {
                handleLineWidth: 1,
                railSize: 7,
                handleSize: 11,
                borderRadiusXS: 0,
                handleLineWidthHover: 2,
                handleSizeHover: 11,
                colorPrimary: sliderColor,
                colorPrimaryBorder: sliderColor,
                colorPrimaryBorderHover: sliderColor,
                railBg: appTheme.token.darkBackground,
                railHoverBg: appTheme.token.darkBackground,
              },
              Tooltip: {
                colorBgSpotlight: appTheme.token.darkBackground,
                colorText: appTheme.token.colorWhite,
                colorTextLightSolid: appTheme.token.colorWhite,
                borderRadius: 4,
                fontSize: 12,
                paddingXS: 7,
                paddingSM: 14,
                controlHeight: 0,
                lineHeight: 1,
                // Normally toolips should be above modals (so that tooltips inside modals are visible)
                // but in this case we want that specific timeline tooltip (which remains visible even when the cursor is not over the timeline) to stay under the modals.
                // Modals have a z-index of 1000, so 999 is right below:
                zIndexPopup: 999,
              },
            },
          }}
        >
          <StyledSlider
            style={{
              cursor: controlDisabled ? 'default' : 'pointer',
            }}
            dots
            dotIndexes={dotIndexes}
            comparison={!!comparison}
            actualHour={actualHour as number}
            max={indexesNumber - 1}
            marks={marks}
            value={currentTime[1]}
            onChange={(value) => {
              if (controlDisabled) return;
              handleCurrentTime(value, true);
              setIsDragging(true);
            }}
            onChangeComplete={() => {
              if (controlDisabled) return;
              setIsDragging(false);
            }}
            tooltip={
              reducedPage
                ? {
                    formatter: () => currentTime[0]?.format('HH:00'),
                    // getPopupContainer: () =>
                    //   document.querySelector('.ant-slider-handle') as HTMLElement,
                    placement: 'top',
                    autoAdjustOverflow: false,
                  }
                : {
                    open: !!currentTime[0],
                    formatter: () =>
                      isDragging
                        ? currentTime[0]?.format('dddd DD MMMM YYYY - HH:00')
                        : currentTime[0]?.format('HH:00'),
                    // getPopupContainer: () =>
                    //   document.querySelector('.ant-slider-handle') as HTMLElement,
                    placement: 'bottom',
                    autoAdjustOverflow: false,
                  }
            }
          />
          <StyledMarks className="timeline-marks">
            {Array.from({ length: daysNumber }, (_, i) => i).map((step) => (
              <div key={step} className="slider-marks-date">
                <div className="slider-marks-date-inner">
                  {daysNumber > 6
                    ? startDate?.add(step, 'day').format('dd DD')
                    : startDate?.add(step, 'day').format('ddd DD')}
                </div>
              </div>
            ))}
          </StyledMarks>
        </ConfigProvider>
      </div>
      {!controlDisabled && (
        <Button
          className="timeline-play-button"
          type="primary"
          onClick={handleTimeLinePlay}
          icon={
            timelinePlaying ? (
              <PauseOutlined />
            ) : (
              <CaretRightOutlined style={{ marginLeft: '2px' }} />
            )
          }
        />
      )}
    </div>
  );
}
